<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import CartProduct from '~/components/cart/CartProduct.vue'
import ConfirmDeletion from '~/components/modal-windows/ConfirmDeletion.vue'
import { useProduct } from '~/composables/product'
import constants from '~/constants'
import EcommerceApiService from '~/services/ecommerce-api-service'
import { useAuthStore } from '~/store/auth'
import { useCartStore } from '~/store/cart'
import { useProductsStore } from '~/store/products'

const { $apiHelper, $toast } = useNuxtApp()
const authStore = useAuthStore()
const cartStore = useCartStore()
const { createGuestIfNeeded } = useProduct()
const productsStore = useProductsStore()

const messages = {
  failedToDeleteProductFromCart: 'Не удалось удалить товар из корзины.',
  failedToDeleteProductsFromCart: 'Не удалось удалить товары из корзины.',
  failedToUpdateProductQuantity: 'Не удалось обновить количество товара.',
  productsAreDeletedFromCart: 'Товары удалены из корзины.',
  productIsDeletedFromCart: 'Товар удалён из корзины.'
}

const areAllProductsSelected = ref(true)
const needToUpdateAllProducts = ref(true)

const selectedProductsData = computed(
  () => cartStore.cartProductsData.filter(({ meta }): boolean => Boolean(meta.isSelected))
)

watch(areAllProductsSelected, (): void => {
  if (needToUpdateAllProducts.value) {
    for (const { meta } of cartStore.cartProductsData) {
      meta.isSelected = areAllProductsSelected.value
    }
  }
})

async function confirmDeletion (): Promise<void> {
  const { failedToDeleteProductsFromCart, productsAreDeletedFromCart } = messages

  const selectedProductIds = selectedProductsData.value.map(({ id }): string => id)

  cartStore.isDataLoading2 = true

  let isSuccess = false

  try {
    await $apiHelper.cartProducts.deleteCartProducts(selectedProductIds)

    isSuccess = true

    $toast.success(productsAreDeletedFromCart)
  } catch (error) {
    console.error(error)

    $toast.error(failedToDeleteProductsFromCart)
  }

  if (isSuccess) {
    await cartStore.fetchCartProducts()
  }

  cartStore.isDataLoading2 = false
}

async function deleteProductFromCart (productId: string): Promise<void> {
  const { failedToDeleteProductFromCart, productIsDeletedFromCart } = messages

  cartStore.isDataLoading2 = true

  let isSuccess = false

  try {
    await $apiHelper.cartProducts.deleteCartProduct(productId)

    isSuccess = true

    $toast.success(productIsDeletedFromCart)
  } catch (error) {
    console.error(error)

    $toast.error(failedToDeleteProductFromCart)
  }

  if (isSuccess) {
    const cartProductData = cartStore.getCartProductDataById(productId)

    if (cartProductData) {
      EcommerceApiService.removeFromCart(productId, cartProductData.meta.quantity)
    }

    productsStore.removeCartProduct(productId)

    await cartStore.fetchCartProducts()
  }

  cartStore.isDataLoading2 = false
}

async function openConfirmDeletionModalWindow (): Promise<void> {
  await useModal({
    component: ConfirmDeletion,
    attrs: {
      title: constants.confirmDeletionTexts.cartProducts,
      onConfirmDeletion: confirmDeletion
    }
  }).open()
}

async function setProductQuantity (productId: string, quantity: number): Promise<void> {
  if (quantity > 1000) {
    quantity = 1000
  }

  const cartProductData = cartStore.getCartProductDataById(productId)

  if (!cartProductData) {
    return
  }

  const oldQuantity = cartProductData.meta.quantity
  cartProductData.meta.quantity = quantity

  cartStore.discountAmountInKopecks = 0

  cartStore.isDataLoading2 = true

  let isSuccess = false

  try {
    if (!authStore.isUser) {
      await createGuestIfNeeded()
    }

    await $apiHelper.cartProducts.updateCartProduct(productId, quantity)

    productsStore.setCartProductQuantity({ id: productId, quantity })

    isSuccess = true
  } catch (error) {
    console.error(error)

    cartProductData.meta.quantity = oldQuantity

    $toast.error(messages.failedToUpdateProductQuantity)
  }

  if (isSuccess) {
    await cartStore.fetchCartProducts()
  }

  cartStore.isDataLoading2 = false
}

async function toggleProductIsSelected (productId: string, isSelected: boolean): Promise<void> {
  const cartProductData = cartStore.getCartProductDataById(productId)

  if (cartProductData) {
    cartProductData.meta.isSelected = isSelected
  }

  needToUpdateAllProducts.value = false

  areAllProductsSelected.value = selectedProductsData.value.length === cartStore.cartProductsData.length

  await nextTick(() => {
    needToUpdateAllProducts.value = true
  })
}
</script>

<template>
  <div class="rounded-xl bg-white p-6">
    <div class="flex items-center justify-between border-b border-solid border-[rgba(0,0,0,.05)] pb-3">
      <div>
        <a
          v-show="selectedProductsData.length"
          class="cursor-pointer select-none text-sm text-[#434242] opacity-[.3] hover:underline"
          href="#"
          @click.prevent="openConfirmDeletionModalWindow"
        >
          Удалить выбранные
        </a>
      </div>

      <NewCheckbox
        v-model="areAllProductsSelected"
        class="!text-sm"
        label-text="Выбрать все"
        name="areAllProductsSelected"
      />
    </div>

    <CartProduct
      v-for="x in cartStore.cartProductsData"
      :key="x.id"
      :cart-product-data="x"
      class="border-b border-[rgba(0,0,0,.05)] last:!border-none"
      @delete-product-from-cart="deleteProductFromCart"
      @set-product-quantity="setProductQuantity"
      @toggle-product-is-selected="toggleProductIsSelected"
    />
  </div>
</template>
